import { useCallback, useEffect, useMemo } from 'react';

import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { setComponentMultiAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useStudentGetAnswers } from 'approot/activity-builder/student/answer/student-answer.hooks';
import {
  ACTIVITIES_ANSWER_MODE_FREE,
  ACTIVITIES_ANSWER_MULTIPLE_CHOICE_TYPE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
} from 'global/constants';
import customNanoId from 'lib/nanoid/nanoid';
import { useBatchStudentAnswersContext } from '../shared/batch-student-answer.context';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import SubActivityView from '../sub-activity/sub-activity-view';
import MultipleChoiceViewOptions from './multiple-choice-view-options';
import { ViewProps } from './multiple-choice.constants';
import ComponentLoading from '../shared/component-loading';
import MultipleChoiceStudentCheckAnswer from './student/multiple-choice-student-check-answer';
import { AnswerSaveStatus } from '../shared/answer-save-status/answer-save-status';

const MultipleChoiceStudentLesson = (
  props: ViewProps & {
    isLoggedIn: boolean;
  }
) => {
  const { subActivityProperties, id, properties, sectionId, isLoggedIn, type } =
    props;

  const {
    isZoomToFit,
    isPublished,
    isLesson,
    activeComponent,
    setActiveComponent,
  } = useActivityBuilderPresentContext();
  const { currentAnswerData } = useCheckAnswer({
    componentId: id,
  });

  const { multipleChoice } = properties;
  const isFreeAnswer =
    multipleChoice.answerMode === ACTIVITIES_ANSWER_MODE_FREE;

  const activityId = subActivityProperties?.activityComponentId;

  const selectedAnswers = useMemo(
    () => currentAnswerData?.multiAnswer?.answerRefs || [],
    [currentAnswerData?.multiAnswer?.answerRefs]
  );
  const correctAnswers = useMemo(
    () => currentAnswerData?.multiAnswer?.correctRefs || [],
    [currentAnswerData?.multiAnswer?.correctRefs]
  );

  const { variables, componentAnswers, hasFetchedAnswers, error } =
    useStudentGetAnswers({
      componentId: id,
      skip: !isLoggedIn,
    });

  const answerData = componentAnswers?.at(0);

  const { handleUpdate } = useBatchStudentAnswersContext();

  const handleSubmitAnswer = useCallback(() => {
    if (isLoggedIn && activityId && isPublished && variables) {
      handleUpdate({
        variables,
        dataTrackingComponentType:
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
        input: {
          __typename: 'ActivityAnswer',
          uuid: customNanoId(),
          activityId,
          answerType: ACTIVITIES_ANSWER_MULTIPLE_CHOICE_TYPE,
          componentId: id,
          createdAt: new Date(),
          updatedAt: new Date(),
          studentId: null,
          answerReferences: null,
          answer: {
            __typename: 'MultipleChoiceAnswer',
            multipleChoiceItems: multipleChoice.options.map(opt => ({
              __typename: 'MultipleChoiceAnswerItem',
              itemId: opt.itemId || '',
              isSelected: selectedAnswers.includes(opt.itemId!),
              isCorrect: isFreeAnswer
                ? null
                : correctAnswers.includes(opt.itemId!) &&
                  opt.correctAnswerBoolean,
            })),
          },
        },
      });
    }
  }, [
    id,
    selectedAnswers,
    correctAnswers,
    multipleChoice,
    isFreeAnswer,
    activityId,
    isPublished,
    isLoggedIn,
    variables,
    handleUpdate,
  ]);

  useEffect(() => {
    if (
      !currentAnswerData?.hasReset &&
      !currentAnswerData?.multiAnswer?.answerRefs?.length
    ) {
      const multiAnswer = (
        componentAnswers?.[0]?.answer as MultipleChoiceAnswerFragment
      )?.multipleChoiceItems;

      if (multiAnswer) {
        setComponentMultiAnswer({
          sectionId: sectionId,
          componentId: id,
          answers: multiAnswer
            .filter(answer => answer.isSelected)
            .map(answer => answer.itemId),
          checkAnswerData: checkAnswerVar(),
          hasChecked: true,
        });
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentAnswers?.length, currentAnswerData, id, sectionId]);

  return (
    <SubActivityView
      subActivityDetails={subActivityProperties}
      componentId={id}
      componentType={type}
      onClick={() => setActiveComponent({ id })}
      additionalMenuItems={
        <MultipleChoiceStudentCheckAnswer
          id={id}
          isFreeAnswer={isFreeAnswer}
          moreInformation={multipleChoice.moreInformation}
          onSubmitAnswer={handleSubmitAnswer}
        />
      }
    >
      {activeComponent?.id === id && answerData && (
        <AnswerSaveStatus position="left" />
      )}

      <div id={getComponentSelectorId(id)}>
        <MultipleChoiceViewOptions
          {...props}
          isResponsive={!isZoomToFit}
          showAnswer={isLesson}
          onSelectOption={() => setActiveComponent({ id })}
          data-selector="present-multi-choice"
        />
      </div>
      <div id={`check-answer-${id}`} />

      {isLoggedIn && (
        <ComponentLoading hasData={hasFetchedAnswers} error={error} />
      )}
    </SubActivityView>
  );
};

export default MultipleChoiceStudentLesson;
